import { useCallback, useEffect, useRef, useState } from 'react';
import { UseInfiniteScrollProps } from './props';
import { debounce } from 'lodash';

/**
 * Custom hook to implement infinite scrolling functionality within a container.
 * It listens to the scroll event and triggers a callback to load more content
 * when the user scrolls near the bottom of the container.
 *
 * @param {UseInfiniteScrollProps} props - The properties for the infinite scroll hook.
 * @param {boolean} props.hasMore - Indicates if there are more items to load.
 * @param {boolean} props.isLoading - Indicates if a loading operation is currently in progress.
 * @param {Function} props.loadMore - The function to call to load more items.
 * @param {boolean} [props.condition=true] - An additional condition to check before loading more items.
 * @param {boolean} [props.reverse=false] - If true, loads more items when scrolled to the top.
 *
 * @returns {Object} - An object containing a ref to attach to the scrollable container and a boolean indicating if the scroll is at the bottom.
 * @returns {React.RefObject<any>} ref - A ref to attach to the scrollable container.
 * @returns {boolean} isAtBottom - A boolean indicating if the scroll is at the bottom of the container.
 *
 * Example usage:
 * const { ref, isAtBottom } = useInfiniteScroll({
 *   hasMore: true,
 *   isLoading: false,
 *   loadMore: () => fetchMoreItems(),
 *   condition: true,
 *   reverse: false,
 * });
 */
export function useInfiniteScroll({
  hasMore,
  isLoading,
  loadMore,
  condition = true,
  reverse = false,
}: UseInfiniteScrollProps) {
  // Reference to the scrollable container element
  const ref = useRef<any>(null);
  // State to track the distance from the bottom of the container
  const [distanceBottom, setDistanceBottom] = useState<number>(0);
  // State to track if the scroll is at the bottom of the container
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);

  /**
   * Memoized scroll event listener that checks if more content should be loaded.
   * It uses lodash's debounce to pageSize the rate at which the function is called.
   */
  const scrollListener = useCallback(
    debounce(() => {
      if (ref && ref.current) {
        const scrollTop = ref?.current?.scrollTop;
        const bottom = ref.current?.scrollHeight - ref.current?.clientHeight;

        // Set the distance from the bottom if not already set
        if (!distanceBottom) {
          setDistanceBottom(Math.round(bottom * 0.2));
        }

        // Load more items if scrolled to the top and conditions are met
        if (scrollTop === 0 && hasMore && !isLoading && condition && reverse) {
          loadMore();
        }

        // Load more items if scrolled near the bottom and conditions are met
        if (
          scrollTop > bottom - distanceBottom &&
          hasMore &&
          !isLoading &&
          condition &&
          !reverse
        ) {
          loadMore();
        }

        // Update the isAtBottom state based on scroll position
        if (scrollTop > bottom - distanceBottom) {
          setIsAtBottom(true);
        } else {
          setIsAtBottom(false);
        }
      }
    }, 100),
    [hasMore, loadMore, isLoading, distanceBottom, reverse, condition, ref],
  );

  /**
   * Effect hook to add the scroll event listener when the component mounts
   * and remove it when the component unmounts.
   */
  useEffect(() => {
    if (!(ref && ref?.current)) {
      return;
    }
    const element = ref?.current;

    // Add scroll event listener
    element?.addEventListener('scroll', scrollListener);
    // Cleanup function to remove the event listener
    return () => {
      element?.removeEventListener('scroll', scrollListener);
    };
  }, [scrollListener, ref]);

  // Return the ref to the scrollable container and the isAtBottom state
  return {
    ref,
    isAtBottom,
  };
}

export type { UseInfiniteScrollProps };
